// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { createReference, getReferenceString } from '@medplum/core';
import type {
  Appointment,
  AppointmentParticipant,
  ChargeItem,
  CodeSystem,
  Encounter,
  Resource,
  Slot,
} from '@medplum/fhirtypes';
import { APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM } from '@polarissleep/shared';
import { parseAppointmentTypeCodeSystem } from './appointment-type-parsing';
import { APPOINTMENT_TYPE_SYSTEM } from './appointment-types';
import { blockOverlappingSlots } from './booking';
import { cancelAppointment } from './cancellation';
import type { RescheduleAppointmentResult } from './types';

/** Extension URL on the new appointment referencing the original it replaces */
export const REPLACES_APPOINTMENT_EXTENSION_URL =
  'http://polarissleep.com/fhir/StructureDefinition/replaces-appointment';

/** Extension URL on the old appointment referencing the new one that replaced it */
export const REPLACED_BY_APPOINTMENT_EXTENSION_URL =
  'http://polarissleep.com/fhir/StructureDefinition/replaced-by-appointment';

interface RescheduleAppointmentParams {
  /** ID of the existing booked appointment to reschedule */
  appointmentId: string;
  /** FHIR Slot resource ID for the new time */
  newSlotId: string;
  /** New appointment start time (ISO datetime) */
  newStart: string;
  /** New appointment end time (ISO datetime) */
  newEnd: string;
  /**
   * Optional transform applied to each newly created resource (Appointment, Encounter)
   * before it is persisted. Use this to tenant-scope writes (e.g. set `meta.account`)
   * so they satisfy compartment-based AccessPolicy criteria. Defaults to identity.
   */
  wrapResource?: <T extends Resource>(resource: T) => T;
}

/**
 * Reschedules an appointment by booking a new slot first, then cancelling the old one.
 * Links the two appointments via FHIR extensions to preserve clinical history.
 *
 * Uses a book-first strategy: the new slot is claimed before the old appointment is
 * cancelled. If the old cancellation fails, the patient still has their new appointment
 * (worst case: two booked appointments, easily resolved manually).
 *
 * @param medplum - The Medplum client
 * @param params - Reschedule parameters
 * @returns Result with new and old appointment/encounter IDs
 */
export async function rescheduleAppointment(
  medplum: MedplumClient,
  params: RescheduleAppointmentParams
): Promise<RescheduleAppointmentResult> {
  const { appointmentId, newSlotId, newStart, newEnd } = params;
  const wrapResource = params.wrapResource ?? (<T extends Resource>(r: T): T => r);

  // Step 1: Read and validate the old appointment
  const oldAppointment = await medplum.readResource('Appointment', appointmentId);
  if (oldAppointment.status !== 'booked') {
    throw new Error('Only booked appointments can be rescheduled.');
  }

  // Extract appointment type and participants from the old appointment
  const appointmentTypeCode = oldAppointment.appointmentType?.coding?.[0]?.code;
  if (!appointmentTypeCode) {
    throw new Error('Existing appointment is missing an appointment type.');
  }

  const patientParticipant = oldAppointment.participant?.find((p: AppointmentParticipant) =>
    p.actor?.reference?.startsWith('Patient/')
  );
  const providerParticipant = oldAppointment.participant?.find((p: AppointmentParticipant) =>
    p.actor?.reference?.startsWith('Practitioner/')
  );

  if (!patientParticipant?.actor?.reference || !providerParticipant?.actor?.reference) {
    throw new Error('Existing appointment is missing patient or provider participant.');
  }

  // Step 2: Read and validate the new slot
  const newSlot = await medplum.readResource('Slot', newSlotId);
  if (newSlot.status !== 'free') {
    throw new Error('The selected time slot is no longer available.');
  }
  if (newSlot.start && new Date(newSlot.start).getTime() <= Date.now()) {
    throw new Error('The selected time slot has already passed. Please select a future time.');
  }

  // Step 3: Resolve appointment type definition for Encounter class
  const codeSystem = await medplum.searchOne('CodeSystem', { url: APPOINTMENT_TYPE_SYSTEM });
  if (!codeSystem) {
    throw new Error('Appointment type configuration not found.');
  }
  const appointmentTypes = parseAppointmentTypeCodeSystem((codeSystem as CodeSystem).concept ?? []);
  const appointmentType = appointmentTypes.find((t) => t.coding.code === appointmentTypeCode);
  if (!appointmentType) {
    throw new Error(`Unknown appointment type: ${appointmentTypeCode}`);
  }

  // Step 4: Claim the new slot (atomic lock via meta.versionId)
  let claimedSlot: Slot;
  try {
    claimedSlot = await medplum.updateResource<Slot>({
      ...newSlot,
      status: 'busy',
    });
  } catch {
    throw new Error('The selected time slot is no longer available. Please select another time.');
  }

  // Step 5: Create new Appointment and Encounter.
  // Only the creates themselves are in the rollback-on-failure region — if either
  // throws, we release the claimed slot and surface the error. Once both creates
  // commit, the reschedule is considered "done from the patient's perspective":
  // any subsequent cleanup failure (block-overlaps, cancel-old, link-extension)
  // is logged but must NOT fail the flow, because rolling the claimed slot back
  // to 'free' while the new Appointment still references it would leave the
  // system in a worse state than just reporting a partial success.
  let newAppointment: Appointment;
  let newEncounter: Encounter;
  try {
    newAppointment = await medplum.createResource(
      wrapResource<Appointment>({
        resourceType: 'Appointment',
        status: 'booked',
        start: newStart,
        end: newEnd,
        appointmentType: oldAppointment.appointmentType,
        slot: [createReference(claimedSlot)],
        participant: oldAppointment.participant,
        extension: [
          {
            url: REPLACES_APPOINTMENT_EXTENSION_URL,
            valueReference: { reference: `Appointment/${appointmentId}` },
          },
        ],
      })
    );

    newEncounter = await medplum.createResource(
      wrapResource<Encounter>({
        resourceType: 'Encounter',
        status: 'planned',
        class: {
          system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
          code: appointmentType.mode === 'virtual' ? 'VR' : 'AMB',
          display: appointmentType.mode === 'virtual' ? 'virtual' : 'ambulatory',
        },
        type: [{ coding: [appointmentType.coding], text: appointmentType.display }],
        subject: { reference: patientParticipant.actor.reference },
        appointment: [createReference(newAppointment)],
        participant: [
          {
            individual: { reference: providerParticipant.actor.reference },
          },
        ],
      })
    );
  } catch (err) {
    // Rollback: release the new slot — nothing has been committed that
    // references it yet, so it's safe to flip back to free.
    try {
      await medplum.updateResource<Slot>({
        ...claimedSlot,
        status: 'free',
      });
    } catch {
      // Best-effort rollback
    }
    throw err;
  }

  // Step 6: Best-effort overlap blocking. A failure here (e.g. lacking write
  // access on peer slots) would leave potential double-bookings but must not
  // invalidate the just-committed new Appointment.
  try {
    await blockOverlappingSlots(medplum, claimedSlot);
  } catch (err) {
    console.warn('rescheduleAppointment: blockOverlappingSlots failed (non-fatal)', err);
  }

  // Step 7: Best-effort cancellation of the old appointment. The book-first
  // strategy explicitly tolerates this failing — worst case the patient has
  // two booked appointments and it's resolved manually. `oldSlotReleased` /
  // `oldSlotsRestored` in the return value reflect what actually happened.
  let oldSlotReleased = false;
  let oldSlotsRestored = 0;
  try {
    const cancelResult = await cancelAppointment(medplum, appointmentId, 'Rescheduled');
    oldSlotReleased = cancelResult.slotReleased;
    oldSlotsRestored = cancelResult.slotsRestored;
  } catch (err) {
    console.warn('rescheduleAppointment: cancelAppointment failed (non-fatal)', err);
  }

  // Step 8: Add replaced-by extension on the old appointment — purely informational
  try {
    const cancelledOld = await medplum.readResource('Appointment', appointmentId);
    const existingExtensions = cancelledOld.extension ?? [];
    await medplum.updateResource<Appointment>({
      ...cancelledOld,
      extension: [
        ...existingExtensions,
        {
          url: REPLACED_BY_APPOINTMENT_EXTENSION_URL,
          valueReference: { reference: getReferenceString(newAppointment) },
        },
      ],
    });
  } catch (err) {
    // Non-critical — the link is nice-to-have, don't fail the reschedule for it
    console.warn('rescheduleAppointment: failed to write replaced-by extension (non-fatal)', err);
  }

  // Step 9: Keep ChargeItem.occurrenceDateTime in sync with the new appointment start.
  // Best-effort — a failure here must not roll back the completed reschedule.
  try {
    const charges = await medplum.searchResources('ChargeItem', {
      identifier: `${APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM}|${appointmentId}`,
      _count: '1',
    });
    const charge = charges[0] as ChargeItem | undefined;
    if (charge?.id && newAppointment.id) {
      await medplum.updateResource<ChargeItem>({
        ...charge,
        occurrenceDateTime: newStart,
        identifier: [
          ...(charge.identifier?.filter((i) => i.system !== APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM) ?? []),
          { system: APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, value: newAppointment.id },
        ],
      });
    }
  } catch (err) {
    console.warn('rescheduleAppointment: ChargeItem occurrenceDateTime sync failed (non-fatal)', err);
  }

  return {
    newAppointmentId: newAppointment.id as string,
    newEncounterId: newEncounter.id as string,
    oldAppointmentId: appointmentId,
    oldSlotReleased,
    oldSlotsRestored,
  };
}
